<template>
    <div class="card">
        <b-table responsive striped hover :items="items" :fields="headers" caption-top show-details :per-page="perPage">
            <!-- Columna con la fecha del error  -->
            <template #cell(fechaCreacion)="data">
                <div style="text-align: center;">{{ data.item.fechaCreacion }}
                </div>
            </template>
            <!-- Columna con el mensaje del error  -->
            <template #cell(mensaje)="data">
                <div class="formatoMensaje" :style="getStyle(data.item)">
                    <b-icon
                        :icon="getIcon(data.item)"
                        scale="1.10"
                        :style="{ color: getColor(data.item) }">
                    </b-icon>
                    {{ data.item.mensaje.substring(0, 18) + "..." }}
                </div>
            </template>
            <!-- Columna con la fecha del error  -->
            <template #cell(usuario)="data">
                <div style="text-align: center;">{{ data.item.usuario }}
                </div>
            </template>
            <!-- Botón para Mostrar Detalles -->
            <template #cell(_showDetails)="data">
                <div class="d-flex justify-content-center ">
                    <b-button
                        id="btnDatails"
                        size="sm"
                        class="btnMoreInfo"
                        @click="toggleRowDetails(data.item)">
                        {{ data.item._showDetails ? 'Ocultar' : 'Ver' }} Detalles
                    </b-button>
                </div>
            </template>
            <!-- Fila de Detalles -->
            <template #row-details="data">
                    <p><strong>Mensaje:</strong> {{ data.item.mensaje }}</p>
                    <p><strong>Stack Trace:</strong> {{ data.item.stackTrace }}</p>
                    <p><strong>Dispositivo:</strong> {{ data.item.infoDispositivo }}</p>
            </template>
        </b-table>
        <b-pagination
            variant="secondary"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            align="center"
            class="my-0">
        </b-pagination>
    </div>
</template>

<script>
export default {
    props:{
        /**
         * Variable que almacena la informacion a renderizar dentro de la tabla.
         */
        items: {
            type: [Array, Object],
            required: true,
            default: () => ({}),
        },
        /**
         * Variable que almacena los campos/cabeceras/columnas que contendrá la tabla.
         */
        fields: {
            type: [Array, Object],
            required: true,
            default: () => ({}),
        }
    },
    data() {
        return {
            /** Objeto que almacena las cabeceras de la tabla.*/
            headers: [],
            /** Variable que almacena el numero total de items */
            totalRows: 0,
            /** Variable que almacena el número de página actual en la paginación. */
            currentPage: 1,
            /** Variable que almacena el número de filas a mostrar por página. */
            perPage: 20,
        }
    },
    watch: {
        items: {
            immediate: true,
            handler(newItem) {
                if (newItem && Array.isArray(newItem)) {
                    this.addPropertyDetails(newItem);
                    this.totalRows = newItem.length;
                }
            },
        },
        fields: {
            immediate: true,
            handler(newField) {
                if (newField && Array.isArray(newField)) {
                    this.addDetailsField(newField);
                }
            },
        },
    },
    methods: {
        /**
         * Agrega la propiedad `_showDetails` a cada elemento del array.
         * La propiedad `_showDetails` se inicializa a `false`.
         * El propósito de esta propiedad es controlar el momento del renderizado
         * de una fila adicional sobre la tabla.
         *
         * @param {Array} data - Arreglo de elementos a los que se añadirá la propiedad `_showDetails`.
         */
        addPropertyDetails(data) {
            data.forEach(item => {
                this.$set(item, '_showDetails', false);
            });
        },
        /**
         * Agrega el campo adicional 'Detalles' dentro del arreglo de cabeceras
         * para la tabla.
         *
         * @param {Array} fields - El arreglo de cabeceras al que se añadirá el campo de detalles.
         */
        addDetailsField(fields) {
            const newArrayFiles = [...fields, { key: '_showDetails', label: 'Detalles' }];
            this.setPropertySplitField(newArrayFiles);
        },
        /**
         * Establece la propiedad formatter de cada campo en el arreglo de cabeceras proporcionado al método splitText.
         * Actualiza la propiedad headers con el array de campos modificado.
         *
         * @param {Array} fields - Un arreglo de objetos de a modificar.
         */
        setPropertySplitField(fields) {
            fields.forEach(field => {
                field.formatter = this.splitText;
            });
            this.headers = fields;
        },
        /**
         * Divide el texto dado si supera los 18 caracteres y añade una elipsis («...»).
         *
         * @param {string} value - El texto a dividir.
         * @returns {string} - El texto original si su longitud es igual o inferior a 18 caracteres, 
         * en caso contrario, los primeros 18 caracteres seguidos de una elipsis.
         */
        splitText(value) {
            return value && value.length > 18 ? value.substring(0, 18) + "..." : value;
        },
        /**
         * Cambia la visibilidad de los detalles de un elemento de fila dado.
         *
         * @param {Object} item - El elemento de fila para el que se desea cambiar la visibilidad de los detalles.
         * @property {boolean} item._showDetails - Indica si se muestran actualmente los detalles.
         */
        toggleRowDetails(item) {
            this.$set(item, '_showDetails', !item._showDetails);
        },

        /**
         * Devuelve el objeto de estilo para un elemento.
         *
         * @param {Object} item - El elemento de mensaje para el que obtener el estilo.
         * @returns {Objeto} El objeto de estilo que contiene las propiedades backgroundColor, borderColor, color y fontSize.
         */
        getStyle(item) {
            const { border, color } = this.getFormat(item);
            return { backgroundColor: border, borderColor: color, color:color, fontSize: '14px' };
        },
        /**
         * Recupera el icono asociado a un elemento específico.
         *
         * @param {Object} item - El elemento de mensaje del que se desea obtener el icono.
         * @returns {String} - El icono asociado al elemento de mensaje.
         */
        getIcon(item) {
            return this.getFormat(item).icon;
        },
        /**
         * Recupera el color asociado a un elemento dado.
         *
         * @param {Object} item - El elemento de mensaje del que se desea obtener el color.
         * @returns {string} - El color asociado al mensaje.
         */
        getColor(item) {
            return this.getFormat(item).color;
        },
        /**
         * Devuelve un objeto de mensaje formateado basado en la categoría del elemento.
         * El objeto contiene un icono, color y propiedades de borde.
         *
         * @param {Object} item - El objeto item que contiene una propiedad 'categoria'.
         * @param {string} item.categoria - La categoría del elemento (por ejemplo, 'warn', 'error', 'critical').
         * @returns {Objeto} Un objeto con las siguientes propiedades:
         *                  - icon {string}: El nombre del icono correspondiente a la categoría.
         *                  - color {string}: El código de color correspondiente a la categoría.
         *                  - border {string}: El código de color del borde correspondiente a la categoría.
         */
        getFormat(item) {
            const category = item.categoria.toLowerCase();
            if (category.includes('warn')) {
                return { icon: "exclamation-triangle-fill", color: "#ca992e", border: "#FEF3CD" };
            } else if (category.includes('error')) {
                return { icon: "dash-circle-fill", color: "coral", border: "#fcd4b3" };
            } else if (category.includes('critical')) {
                return { icon: "x-circle-fill", color: "red", border: "#F8D7DA" };
            } else {
                return { icon: "question-lg", color: "#197BFF" };
            }
        },
    }
}
</script>

<style>
.card {
    background-color: #fdffff;
    padding: 20px;
    border-radius: 10px;
    box-shadow: 10px 10px 16px 0 rgba(20, 20, 20, 0.2);
}
caption {
    color: #1d2641;
    text-align: left;
    font-weight: 450;
    font-size: 22px;
}
th {
    text-align: center;
}
.formatoMensaje {
    border-radius: 5px;
    padding: 3px !important;
    border-style: solid;
    border-width: 1px;
}

.btnMoreInfo{
    background-color: #0062cc;
    color: white;
    border: none;
    font-weight: 500;
}

.custom-button {
    padding: 1px 1px;
    font-size: 12px;
    background-color: transparent;
    color: black;
}

p{
    margin-top: -10px;
}

</style>